import { Pagination } from "@mui/material";
import {
  AvatarIcon,
  CalendarIcon,
  PersonIcon,
  SewingPinFilledIcon,
} from "@radix-ui/react-icons";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { SearchInput } from "../../components/Widget/SearchInput";
import { UploadButton } from "../../components/Widget/UploadButton";
import { find, store } from "../../services/service";
import { useStoreApp } from "../../store/storeApp";
import {
  ATTRIBUER_MAINTENANCE_EQUIPE,
  ATTRIBUTION_MAINTENANCE_LOT_EQUIPE,
  LISTE_EN_ATTENTE_MAINTENANCE_EQUIPE,
  LISTE_EQUIPE_PRESTATAIRE,
  PRESTATAIRE,
} from "../../Utils/constant";
import { checkTagValidity } from "../../Utils/Utils";
import { getPrioriteBadge } from "./GestionMaintenance";

const AttenteAttributionEquipe = () => {
  const [msg, setMsg] = useState("");
  const [assignModalItem, setAssignModalItem] = useState(null);
  const [selectedTeamPrestataire, setSelectedTeamPrestataire] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [corbeilleMaintenance, setCorbeilleMaintenance] = useState([]);
  const [isloading, setIsLoading] = useState(false);
  const prestataire = useStoreApp();
  const [equipe, setEquipe] = useState([]);

  const getListEquipe = async (prestataireId) => {
    try {
      const res = await find(`${LISTE_EQUIPE_PRESTATAIRE}/${prestataireId}`);
      setEquipe(res?.data?.donnee || []);
    } catch (err) {
      console.error(err);
      toast.error("Erreur de chargement des équipes du prestataire");
      setEquipe([]);
    }
  };
  const getListeEnAttente = async (page, param) => {
    setIsLoading(true);
    await find(
      `${LISTE_EN_ATTENTE_MAINTENANCE_EQUIPE}?page=${page ?? 0}&param=${param ?? ""}&size=8`,
    ).then((res) => {
      setIsLoading(false);
      setCorbeilleMaintenance(res?.data?.donnee || []);
    });
  };

  // Fonction de recherche avec debounce
  const handleSearch = (value) => {
    setSearchTerm(value);
    setIsSearching(true);
    getListeEnAttente(0, value).finally(() => setIsSearching(false));
  };

  const moreEnAttente = (page) => {
    getListeEnAttente(page - 1, searchTerm);
  };

  const handleAssignTeamPrestataire = async () => {
    if (!assignModalItem || !selectedTeamPrestataire) return;

    setIsAssigning(true);
    setMsg("");

    try {
      const res = await store(
        `${ATTRIBUER_MAINTENANCE_EQUIPE}${assignModalItem?.id}/equipe/${selectedTeamPrestataire}`,
        "application/json",
        {},
      );

      if (res.status === 200) {
        getListeEnAttente(0, searchTerm);
        document.getElementById("assign-equipe").close();
        setAssignModalItem(null);
        setSelectedTeamPrestataire("");
        toast.success("Équipe affectée avec succès");
      }
    } catch (err) {
      const errorMsg =
        err?.response?.data?.donnee || "Erreur lors de l'attribution";
      setMsg(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsAssigning(false);
    }
  };

  useEffect(() => {
    prestataire.getAll(PRESTATAIRE);
  }, []);

  useEffect(() => {
    getListeEnAttente(0, "");
  }, []);

  return (
    <div className="">
      {/* Modal Affecter Prestataire */}
      <dialog id="assign-equipe" className="modal">
        <div className="modal-box w-96">
          <h3 className="font-bold text-lg">Affecter à une equipe</h3>
          <p className="text-xs text-gray-500 mb-3 ">
            Sélectionnez une equipe à affecter pour ce rendez-vous.
          </p>
          {msg && <p className="text-red-500 text-sm mb-2">{msg}</p>}
          <div className="mb-3">
            {equipe?.length === 0 ? (
              <p className="text-sm text-gray-500 my-8">
                Aucune equipe disponible pour ce prestataire.
              </p>
            ) : (
              <select
                value={selectedTeamPrestataire}
                onChange={(e) => setSelectedTeamPrestataire(e.target.value)}
                className="select select-bordered w-full"
                disabled={isAssigning}
              >
                <option value="">-- Choisir une equipe --</option>
                {equipe?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.designation}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="modal-action">
            <button
              onClick={handleAssignTeamPrestataire}
              disabled={isAssigning || !selectedTeamPrestataire}
              className={`btn btn-sm ${
                isAssigning || !selectedTeamPrestataire
                  ? "btn-disabled opacity-50"
                  : "bg-main text-black border-main"
              }`}
            >
              {isAssigning ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Affectation...
                </>
              ) : (
                "Affecter"
              )}
            </button>
            <button
              onClick={() => {
                document.getElementById("assign-equipe").close();
                setAssignModalItem(null);
                setMsg("");
              }}
              disabled={isAssigning}
              className="btn btn-sm"
            >
              Annuler
            </button>
          </div>
        </div>
      </dialog>

      <div className="bg-white rounded-md px-2 py-4 mt-3">
        {/* Filtres */}
        <div className="flex items-center gap-3 mb-4">
          <SearchInput
            onChange={(e) => handleSearch(e.target.value)}
            value={searchTerm}
            isSearching={isSearching}
          />
          <UploadButton
            title="Attribution par lot"
            uploadUrl={ATTRIBUTION_MAINTENANCE_LOT_EQUIPE}
            fetchData={() => getListeEnAttente(0, "")}
            model={null}
          />
        </div>

        {/* Grille des rendez-vous */}
        {isloading ? (
          <div className="flex flex-col justify-center items-center py-20 space-y-3">
            <Loader2 className="w-8 h-8 animate-spin text-main" />
            <span className="ml-3 text-gray-600 text-sm">
              Chargement des données...
            </span>
          </div>
        ) : corbeilleMaintenance?.data?.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-sm text-gray-500">Aucune donnée disponible</p>
          </div>
        ) : (
          <>
            <div className="grid lg:grid-cols-3 md:grid-cols-3 sm:grid-cols-2 mt-4 grid-cols-1 gap-5">
              {corbeilleMaintenance?.data?.map((item) => (
                <div
                  key={item.id}
                  className={`relative bg-white border  rounded-xl shadow-sm hover:shadow-md transition-shadow`}
                >
                  <div className="rounded-t-xl flex items-center gap-3 border-b border-dashed border-amber-400 px-2 py-3">
                    <p className="text-[12px] font-semibold">N° {item?.id}</p>
                    <span className={getPrioriteBadge(item.priorite)}>
                      {item.priorite}
                    </span>
                  </div>
                  <div className="px-3 py-2">
                    <div className="items-center gap-2 space-y-3">
                      <div className="flex gap-1 my-3">
                        <PersonIcon className="bg-gray-100 rounded-full w-8 h-8 p-2" />
                        <div>
                          <p className="text-[13px] font-medium">
                            {item?.nomPrenomsClient}
                          </p>
                          <p className="text-xs flex font-semibold gap-2">
                            <span>{item?.contactClient}</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div className=" w-fit px-1 py-[2px]">
                          {item?.dateCreation && (
                            <p className="text-xs flex gap-1 items-center font-medium">
                              <CalendarIcon />
                              {new Date(item?.dateCreation).toLocaleDateString(
                                "fr-FR",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="gap-1">
                        <div>
                          <p className="text-[13px] font-medium">
                            Numero FTTH : {item?.numeroFtth}
                          </p>
                        </div>
                      </div>

                      <h1 className="text-xs flex gap-1 items-center my-2">
                        <AvatarIcon />
                        Rapporteur : {item?.rapporteur?.agent?.nomPrenoms}
                      </h1>
                      {item.description && (
                        <div className="dropdown flex items-center">
                          <p className="text-xs">{item?.description}</p>
                        </div>
                      )}
                    </div>

                    {item?.commune && (
                      <h1 className="my-2 text-[13px] font-medium flex items-center">
                        <SewingPinFilledIcon />
                        {item?.commune?.designation}
                      </h1>
                    )}
                    {item?.raison && (
                      <div className="my-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-xs text-red-700 font-semibold">
                          Raison:
                        </p>
                        <p className="text-xs text-red-600 mt-1">
                          {item?.raison}
                        </p>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <p className="bg-blue-50 text-blue-900 py-1 px-1 rounded-lg font-medium text-xs truncate">
                        {item?.statut?.toLowerCase().replace(/_/g, " ")}
                      </p>
                    </div>

                    <div className="flex items-center gap-1">
                      {checkTagValidity("ATTRIBUER_RDV_PRESTATAIRE") && <></>}

                      <button
                        onClick={() => {
                          setAssignModalItem(item);
                          setMsg("");
                          getListEquipe(item?.prestataire?.id);
                          document.getElementById("assign-equipe").showModal();
                        }}
                        className="btn btn-sm bg-main border-main font-mtn text-xs font-regular hover:bg-opacity-90 transition-colors"
                      >
                        {item?.equipe === null ? "Attribuer" : "Réaffecter"}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <div className="my-3 flex justify-end">
          <Pagination
            onChange={(event, newValue) => moreEnAttente(newValue)}
            count={corbeilleMaintenance.totalPages}
            variant="outlined"
            color="primary"
            shape="rounded"
          />
        </div>
      </div>
    </div>
  );
};

export default AttenteAttributionEquipe;
